import { useCallback, useState } from "react"

import type { ProductTestFormPayload } from "../types"

import type { CreateFileRecordInput, FieldGroupEnum } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { useFileUpload } from "@/hooks/useFileUpload"
import { useMultiFileUpload } from "@/hooks/useMultiFileUpload"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UpdateProductTestDocumentsParams
  extends Partial<Pick<ProductTestFormPayload, "documentsToUpload" | "documentsToDelete" | "documentsToCopy">> {
  productTestId: string
  isLotUpdated?: boolean
}

export interface UseUpdateProductTestDocumentsReturn {
  updateDocuments: (params: UpdateProductTestDocumentsParams) => Promise<boolean>
  isUpdatingDocuments: boolean
}

export const useUpdateProductTestDocuments = (): UseUpdateProductTestDocumentsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { createRecord: createFileRecord } = useFileUpload()
  const { removeSingleFile } = useFilesRemove()
  const [isModifyingDocuments, setIsModifyingDocuments] = useState(false)
  const { isUploading: isUploadingDocuments, uploadFiles } = useMultiFileUpload({
    referenceTableName: ReferenceTableName.ProductTestDocument,
  })

  const updateDocuments = useCallback(
    async ({
      productTestId,
      isLotUpdated = false,
      documentsToUpload = [],
      documentsToDelete = [],
      documentsToCopy = [],
    }: UpdateProductTestDocumentsParams): Promise<boolean> => {
      let hasFailure = false

      setIsModifyingDocuments(true)

      if (isLotUpdated) {
        for (const document of documentsToCopy) {
          const { __typename, createdOn, isArchived, ...fileRecordValues } = document.file!
          const isCopied = await createFileRecord({
            ...fileRecordValues,
            fileId: crypto.randomUUID(),
            referenceId: productTestId,
          } as CreateFileRecordInput)

          if (!isCopied) {
            hasFailure = true
            notify({ type: NotificationType.Error, message: "Failed to copy a document." })
          }
        }
      } else {
        for (const fileId of documentsToDelete) {
          const isRemoved = await removeSingleFile(fileId)

          if (!isRemoved) {
            hasFailure = true
            notify({ type: NotificationType.Error, message: "Failed to remove a document." })
          }
        }
      }

      setIsModifyingDocuments(false)

      if (documentsToUpload.length) {
        const { failures } = await uploadFiles(
          documentsToUpload.map((document, index) => ({
            id: String(index),
            file: document.file!,
            fieldGroup: document.documentType as FieldGroupEnum,
            label: document.description ?? "",
          })),
          { referenceIds: [productTestId] }
        )

        hasFailure ||= failures.length > 0
      }

      return hasFailure
    },
    [createFileRecord, removeSingleFile, notify, uploadFiles]
  )

  return { updateDocuments, isUpdatingDocuments: isModifyingDocuments || isUploadingDocuments }
}
